// ===========================================
// Hybrid Pattern Card Component
// ===========================================

import { Paper, Typography, Grid, Chip, Divider, Box } from '@mui/material';

interface HybridPattern {
  patternType: string;
  individualSlotDuration: number;
  groupWeeks: number[];
  individualWeeks: number[];
  bookingDeadlineHours: number;
  bookingsOpen: boolean;
}

interface HybridPatternCardProps {
  pattern: HybridPattern;
  /** When the class is in a stream that overrides the clinic duration. */
  streamName?: string | null;
  streamSlotDuration?: number | null;
}

export default function HybridPatternCard({ pattern, streamName, streamSlotDuration }: HybridPatternCardProps) {
  // A stream's duration overrides the pattern's (mirrors backend resolveClinicDuration).
  const effectiveDuration = streamSlotDuration ?? pattern.individualSlotDuration;
  const isOverridden = streamSlotDuration != null && streamSlotDuration !== pattern.individualSlotDuration;
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Hybrid Pattern
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Pattern Type
          </Typography>
          <Typography>{pattern.patternType}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Individual Slot Duration
          </Typography>
          <Typography>
            {effectiveDuration} mins
            {isOverridden && (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}(from {streamName ?? 'stream'})
              </Typography>
            )}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Group Weeks
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(pattern.groupWeeks as number[]).map((week) => (
              <Chip key={week} label={`W${week}`} size="small" color="success" />
            ))}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Individual Weeks
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(pattern.individualWeeks as number[]).map((week) => (
              <Chip key={week} label={`W${week}`} size="small" color="primary" />
            ))}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Booking Deadline
          </Typography>
          <Typography>{pattern.bookingDeadlineHours} hours before</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Bookings Status
          </Typography>
          <Chip
            label={pattern.bookingsOpen ? 'Open' : 'Closed'}
            size="small"
            color={pattern.bookingsOpen ? 'success' : 'default'}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
